import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { Users, MessageSquare, Twitter, Instagram, Youtube, Heart, Share2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const socialLinks = [
  { name: "Discord", icon: MessageSquare, url: "#", members: "5,200+", color: "bg-[#5865F2]" },
  { name: "Twitter", icon: Twitter, url: "#", members: "12,000+", color: "bg-[#1DA1F2]" },
  { name: "Instagram", icon: Instagram, url: "#", members: "8,500+", color: "bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45]" },
  { name: "YouTube", icon: Youtube, url: "#", members: "3,200+", color: "bg-[#FF0000]" },
];

const communityPosts = [
  {
    user: "creativemind_sarah",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
    image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600&h=600&fit=crop",
    caption: "Ma première création avec Floop Pro ! J'adore le rendu 🎨✨",
    likes: 234,
    comments: 42,
  },
  {
    user: "digitalartist_max",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop",
    image: "https://images.unsplash.com/photo-1541701494587-cb58502866ab?w=600&h=600&fit=crop",
    caption: "Exploration de styles abstraits avec Floop. Les possibilités sont infinies !",
    likes: 189,
    comments: 28,
  },
  {
    user: "photo_julie",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=600&fit=crop",
    caption: "Paysage de montagne généré en 5 secondes. Impressionnant ! 🏔️",
    likes: 412,
    comments: 67,
  },
  {
    user: "art_studio_paris",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop",
    image: "https://images.unsplash.com/photo-1633477189729-9290b3261d0a?w=600&h=600&fit=crop",
    caption: "Notre studio utilise Floop pour le prototypage. Gain de temps énorme !",
    likes: 156,
    comments: 23,
  },
];

const stats = [
  { value: "50,000+", label: "Créateurs actifs" },
  { value: "1M+", label: "Images générées" },
  { value: "4.9/5", label: "Note moyenne" },
  { value: "24/7", label: "Support communauté" },
];

const Community = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-16 text-center">
        <div className="container mx-auto px-6">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-display">
            Rejoignez la communauté{" "}
            <span className="bg-gradient-floop bg-clip-text text-transparent">
              Floop
            </span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Partagez vos créations, découvrez celles des autres et apprenez ensemble. 
            Une communauté de créatifs passionnés par l'IA.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="bg-card border border-border rounded-2xl p-4">
                <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Links */}
      <section className="py-12 bg-secondary/30">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            Suivez-nous partout
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.url}
                className="bg-card border border-border rounded-2xl p-6 text-center hover:border-primary/50 transition-colors group"
              >
                <div className={`w-12 h-12 rounded-xl ${social.color} flex items-center justify-center mx-auto mb-3`}>
                  <social.icon className="w-6 h-6 text-white" />
                </div>
                <div className="font-semibold text-foreground">{social.name}</div>
                <div className="text-sm text-muted-foreground">{social.members} membres</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Community Gallery */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-display">
              Créations de la communauté
            </h2>
            <p className="text-muted-foreground">
              Découvrez ce que les membres de notre communauté créent avec Floop.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {communityPosts.map((post, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-2xl overflow-hidden"
              >
                <div className="p-4 flex items-center gap-3">
                  <img
                    src={post.avatar}
                    alt={post.user}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <span className="font-medium text-foreground">@{post.user}</span>
                </div>
                <img
                  src={post.image}
                  alt="Creation"
                  className="w-full aspect-square object-cover"
                />
                <div className="p-4">
                  <div className="flex items-center gap-4 mb-3">
                    <button className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors">
                      <Heart className="w-5 h-5" />
                      <span className="text-sm">{post.likes}</span>
                    </button>
                    <button className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors">
                      <MessageSquare className="w-5 h-5" />
                      <span className="text-sm">{post.comments}</span>
                    </button>
                    <button className="ml-auto text-muted-foreground hover:text-primary transition-colors">
                      <Share2 className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-sm text-foreground">{post.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Discord CTA */}
      <section className="py-16 bg-[#5865F2]/10">
        <div className="container mx-auto px-6 text-center">
          <MessageSquare className="w-16 h-16 text-[#5865F2] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-foreground mb-4 font-display">
            Rejoignez notre Discord
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Échangez avec d'autres créateurs, partagez vos astuces, participez à des défis créatifs 
            et obtenez de l'aide en temps réel.
          </p>
          <Button size="lg" className="bg-[#5865F2] hover:bg-[#5865F2]/90 text-white rounded-full px-8">
            <MessageSquare className="w-5 h-5 mr-2" />
            Rejoindre le serveur Discord
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default Community;
